import React, { useState, useEffect, useRef } from "react";
import { getCountryCallingCode } from "libphonenumber-js";
import { usePreferencesStore } from "@/store/usePreferencesStore";

// Comprehensive country list with flags and calling codes
// Moved outside component to prevent recreation on every render
const COUNTRIES = [
  { code: "US", name: "United States", flag: "🇺🇸", callingCode: "1" },
  { code: "IN", name: "India", flag: "🇮🇳", callingCode: "91" },
  { code: "GB", name: "United Kingdom", flag: "🇬🇧", callingCode: "44" },
  { code: "CA", name: "Canada", flag: "🇨🇦", callingCode: "1" },
  { code: "AU", name: "Australia", flag: "🇦🇺", callingCode: "61" },
  { code: "DE", name: "Germany", flag: "🇩🇪", callingCode: "49" },
  { code: "FR", name: "France", flag: "🇫🇷", callingCode: "33" },
  { code: "IT", name: "Italy", flag: "🇮🇹", callingCode: "39" },
  { code: "ES", name: "Spain", flag: "🇪🇸", callingCode: "34" },
  { code: "MX", name: "Mexico", flag: "🇲🇽", callingCode: "52" },
  { code: "BR", name: "Brazil", flag: "🇧🇷", callingCode: "55" },
  { code: "AR", name: "Argentina", flag: "🇦🇷", callingCode: "54" },
  { code: "CN", name: "China", flag: "🇨🇳", callingCode: "86" },
  { code: "JP", name: "Japan", flag: "🇯🇵", callingCode: "81" },
  { code: "KR", name: "South Korea", flag: "🇰🇷", callingCode: "82" },
  { code: "SG", name: "Singapore", flag: "🇸🇬", callingCode: "65" },
  { code: "AE", name: "UAE", flag: "🇦🇪", callingCode: "971" },
  { code: "SA", name: "Saudi Arabia", flag: "🇸🇦", callingCode: "966" },
  { code: "ZA", name: "South Africa", flag: "🇿🇦", callingCode: "27" },
  { code: "NG", name: "Nigeria", flag: "🇳🇬", callingCode: "234" },
  { code: "RU", name: "Russia", flag: "🇷🇺", callingCode: "7" },
  { code: "TR", name: "Turkey", flag: "🇹🇷", callingCode: "90" },
  { code: "PL", name: "Poland", flag: "🇵🇱", callingCode: "48" },
  { code: "NL", name: "Netherlands", flag: "🇳🇱", callingCode: "31" },
  { code: "BE", name: "Belgium", flag: "🇧🇪", callingCode: "32" },
  { code: "SE", name: "Sweden", flag: "🇸🇪", callingCode: "46" },
  { code: "NO", name: "Norway", flag: "🇳🇴", callingCode: "47" },
  { code: "DK", name: "Denmark", flag: "🇩🇰", callingCode: "45" },
  { code: "FI", name: "Finland", flag: "🇫🇮", callingCode: "358" },
  { code: "CH", name: "Switzerland", flag: "🇨🇭", callingCode: "41" },
  { code: "AT", name: "Austria", flag: "🇦🇹", callingCode: "43" },
  { code: "IE", name: "Ireland", flag: "🇮🇪", callingCode: "353" },
  { code: "NZ", name: "New Zealand", flag: "🇳🇿", callingCode: "64" },
  { code: "PK", name: "Pakistan", flag: "🇵🇰", callingCode: "92" },
  { code: "BD", name: "Bangladesh", flag: "🇧🇩", callingCode: "880" },
  { code: "PH", name: "Philippines", flag: "🇵🇭", callingCode: "63" },
  { code: "VN", name: "Vietnam", flag: "🇻🇳", callingCode: "84" },
  { code: "TH", name: "Thailand", flag: "🇹🇭", callingCode: "66" },
  { code: "MY", name: "Malaysia", flag: "🇲🇾", callingCode: "60" },
  { code: "ID", name: "Indonesia", flag: "🇮🇩", callingCode: "62" },
];

/**
 * Smart Email/Phone Input Component
 * Simple text input that detects phone numbers and shows country code
 * NO component switching - always uses regular text input
 */
const SmartEmailPhoneInput = ({
  value,
  onChange,
  error,
  name = "emailOrPhone",
  placeholder = "Email or phone no",
  label = "Email or Phone no",
  className = "",
}) => {
  // Get country code from Zustand store
  const countryCode = usePreferencesStore((state) => state.userCountry);
  const setUserCountry = usePreferencesStore((state) => state.setUserCountry);

  const [showCountryCode, setShowCountryCode] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const dropdownRef = useRef(null);
  const inputRef = useRef(null);

  // Check if value is all digits (phone-like)
  const isPhoneLike = (val) => {
    if (!val) return false;
    if (val.startsWith("+")) return true;
    const cleaned = val.replace(/[\s-]/g, "");
    return cleaned.length >= 2 && /^\d+$/.test(cleaned);
  };

  // Update country code display
  useEffect(() => {
    setShowCountryCode(isPhoneLike(value));
  }, [value]);

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
        setSearchTerm("");
      }
    };

    if (showDropdown) {
      document.addEventListener("mousedown", handleClickOutside);
      return () =>
        document.removeEventListener("mousedown", handleClickOutside);
    }
  }, [showDropdown]);

  // Get country info
  const getCountryInfo = (code) => {
    return (
      COUNTRIES.find((c) => c.code === code) || {
        code: code,
        flag: "🌐",
        callingCode: getCountryCallingCode(code),
      }
    );
  };

  // Filter countries based on search
  const filteredCountries = COUNTRIES.filter(
    (country) =>
      country.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      country.callingCode.includes(searchTerm) ||
      country.code.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Handle country selection
  const handleCountrySelect = (selectedCountryCode) => {
    setUserCountry(selectedCountryCode);
    setShowDropdown(false);
    setSearchTerm("");

    // Update the value with new country code if phone-like
    if (showCountryCode && value) {
      const displayValue = getDisplayValue();
      const newCallingCode = getCountryCallingCode(selectedCountryCode);
      onChange(`+${newCallingCode}${displayValue}`);
    }

    // Focus back on input
    if (inputRef.current) {
      inputRef.current.focus();
    }
  };

  // Get display value (strip country code prefix if present)
  const getDisplayValue = () => {
    if (!value) return "";

    if (value.startsWith("+") && countryCode) {
      try {
        const callingCode = getCountryCallingCode(countryCode);
        if (value.startsWith(`+${callingCode}`)) {
          return value.slice(`+${callingCode}`.length);
        }
      } catch (error) {
        // Fallback to original value
      }
    }

    return value;
  };

  const handleChange = (e) => {
    const inputValue = e.target.value;

    // If typing phone-like digits, add country code prefix
    if (isPhoneLike(inputValue) && countryCode && !inputValue.startsWith("+")) {
      try {
        const callingCode = getCountryCallingCode(countryCode);
        onChange(`+${callingCode}${inputValue}`);
      } catch (error) {
        onChange(inputValue);
      }
    } else {
      onChange(inputValue);
    }
  };

  return (
    <div className="smart-input-wrapper pt-0">
      <div className="input-group">
        {/* Label - customLabel uses absolute positioning */}
        <label className="customLabel">{label}</label>

        {showCountryCode && countryCode && (
          <div
            className="country-badge"
            onClick={() => setShowDropdown(!showDropdown)}
            style={{ cursor: "pointer" }}
          >
            <span className="flag">{getCountryInfo(countryCode).flag}</span>
            <span className="code">+{getCountryCallingCode(countryCode)}</span>
            <span className="dropdown-arrow">▼</span>
          </div>
        )}

        {/* Country Dropdown */}
        {showDropdown && showCountryCode && (
          <div className="country-dropdown" ref={dropdownRef}>
            <div className="dropdown-search">
              <input
                type="text"
                placeholder="Search country..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="search-input"
                autoFocus
              />
            </div>
            <div className="dropdown-list">
              {filteredCountries.map((country) => (
                <div
                  key={country.code}
                  className={`dropdown-item ${
                    country.code === countryCode ? "active" : ""
                  }`}
                  onClick={() => handleCountrySelect(country.code)}
                >
                  <span className="flag">{country.flag}</span>
                  <span className="country-name">{country.name}</span>
                  <span className="calling-code">+{country.callingCode}</span>
                </div>
              ))}
              {filteredCountries.length === 0 && (
                <div className="dropdown-item no-results">
                  No countries found
                </div>
              )}
            </div>
          </div>
        )}

        <input
          ref={inputRef}
          type="text"
          name={name}
          className={`form-control ${
            showCountryCode ? "has-country" : ""
          } ${className} ${error ? "" : ""}`}
          placeholder={placeholder}
          value={getDisplayValue()}
          onChange={handleChange}
          autoComplete="username"
        />
      </div>

      {error && <div className="invalid-feedback d-block">{error}</div>}
    </div>
  );
};

export default SmartEmailPhoneInput;
